import React from 'react'
import { useNavigate } from 'react-router-dom'

const scrollToTopAndNavigate = (navigate, path) => {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  navigate(path);
};

const Home = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-white">
      {/* Diagonal Split Hero Section */}
      <section className="relative w-full min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e8f5e9] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: 'linear-gradient(120deg, #f8fafc 60%, #e8f5e9 100%) skewY(-6deg)'}}></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 px-4 sm:px-8 py-24">
          {/* Left Side: Text */}
          <div className="flex-1 text-center lg:text-left">
            <h1 className="text-6xl sm:text-7xl font-extrabold text-gray-900 mb-4 tracking-tight">Imaginex</h1>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#618A3D] mb-6">AI & Technical Solutions</h2>
            <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-xl mx-auto lg:mx-0">
              Imaginex operates as a fractional CTO for small businesses and private clients. We create technical solutions that allow businesses to grow and scale at a competitive rate. Never let technology be the barrier to your success—compete and scale with confidence.
            </p>
                              <a href="mailto:theimaginexcompany@gmail.com" className="inline-block px-10 py-4 bg-[#618A3D] text-white font-bold rounded-lg shadow-lg hover:bg-[#75a64b] transition text-lg">Get Your Free Consultation</a>
          </div>
          {/* Right Side: Image Placeholder */}
          <div className="flex-1 flex justify-center items-center">
            <img src="/images/Test2.jpg" alt="Woman working on a laptop and AI" className="w-full max-w-lg h-80 object-cover rounded-2xl shadow-lg" />
          </div>
        </div>
      </section>

      {/* Mission/Values Section */}
      <section className="w-full bg-[#f8fafc] border-y border-gray-100 py-16 px-4 sm:px-8 animate__animated animate__fadeInUp">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 text-center">Empowering Small Businesses with Big Ideas</h2>
          <p className="text-lg sm:text-xl text-gray-700 mb-6 max-w-2xl mx-auto text-center">
            We help you compete and grow with tailored technology, design, and automation—built for your business, not the Fortune 500.
          </p>
          <div className="flex flex-col md:flex-row gap-8 justify-center">
            <div className="flex-1 bg-white rounded-xl shadow p-8 text-center flex flex-col items-center">
              <div className="mb-4 w-12 h-12 flex items-center justify-center rounded-full bg-[#e8f5e9]">
                <svg className="w-7 h-7 text-[#618A3D]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m4 0h-1v-4h-1m-4 0h-1v-4h-1m4 0h-1v-4h-1" /></svg>
              </div>
              <h3 className="font-bold text-xl mb-2 text-[#618A3D]">Innovation</h3>
              <p className="text-gray-600">We build custom solutions that help you stand out and grow.</p>
            </div>
            <div className="flex-1 bg-white rounded-xl shadow p-8 text-center flex flex-col items-center">
              <div className="mb-4 w-12 h-12 flex items-center justify-center rounded-full bg-[#e8f5e9]">
                <svg className="w-7 h-7 text-[#618A3D]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3zm0 10c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z" /></svg>
              </div>
              <h3 className="font-bold text-xl mb-2 text-[#618A3D]">Affordability</h3>
              <p className="text-gray-600">High-quality results, accessible to every business owner.</p>
            </div>
            <div className="flex-1 bg-white rounded-xl shadow p-8 text-center flex flex-col items-center">
              <div className="mb-4 w-12 h-12 flex items-center justify-center rounded-full bg-[#e8f5e9]">
                <svg className="w-7 h-7 text-[#618A3D]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2h5" /></svg>
              </div>
              <h3 className="font-bold text-xl mb-2 text-[#618A3D]">Partnership</h3>
              <p className="text-gray-600">We work with you, not just for you. Your success is our mission.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="w-full bg-white py-20 px-4 sm:px-8 animate__animated animate__fadeInUp border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">Our Services</h2>
          <div className="grid gap-10 md:grid-cols-3">
            <button onClick={() => scrollToTopAndNavigate(navigate, '/webdev')} className="group block bg-[#f8fafc] rounded-2xl shadow-lg p-10 hover:shadow-2xl transition border border-gray-100 w-full text-left">
              <div className="mb-6 flex items-center justify-center">
                <svg className="w-12 h-12 text-[#618A3D] group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4h16v16H4z" /></svg>
              </div>
              <h3 className="font-bold text-2xl mb-2 text-[#618A3D]">Website Development</h3>
              <p className="text-gray-700 mb-4">Custom, branded websites with no monthly fees. Own your digital presence.</p>
              <span className="inline-block px-4 py-2 bg-[#618A3D] text-white rounded font-bold mt-2">Learn More</span>
            </button>
            <button onClick={() => scrollToTopAndNavigate(navigate, '/ai')} className="group block bg-[#f8fafc] rounded-2xl shadow-lg p-10 hover:shadow-2xl transition border border-gray-100 w-full text-left">
              <div className="mb-6 flex items-center justify-center">
                <svg className="w-12 h-12 text-[#618A3D] group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m0 0H3" /></svg>
              </div>
              <h3 className="font-bold text-2xl mb-2 text-[#618A3D]">AI Solutions</h3>
              <p className="text-gray-700 mb-4">Automate, optimize, and innovate with custom AI for business and life.</p>
              <span className="inline-block px-4 py-2 bg-[#618A3D] text-white rounded font-bold mt-2">Learn More</span>
            </button>
            <button onClick={() => scrollToTopAndNavigate(navigate, '/marketing')} className="group block bg-[#f8fafc] rounded-2xl shadow-lg p-10 hover:shadow-2xl transition border border-gray-100 w-full text-left">
              <div className="mb-6 flex items-center justify-center">
                <svg className="w-12 h-12 text-[#618A3D] group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8h18M3 16h18" /></svg>
              </div>
              <h3 className="font-bold text-2xl mb-2 text-[#618A3D]">Social Media Marketing</h3>
              <p className="text-gray-700 mb-4">Build your brand, grow your audience, and turn scrolls into sales.</p>
              <span className="inline-block px-4 py-2 bg-[#618A3D] text-white rounded font-bold mt-2">Learn More</span>
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="w-full max-w-4xl mx-auto py-16 px-4 sm:px-8 text-center animate__animated animate__fadeInUp">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Ready to Build Something Amazing?</h2>
        <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">Let’s talk about your goals and how Imaginex can help you achieve them. Your next chapter starts here.</p>
                        <a href="mailto:theimaginexcompany@gmail.com" className="inline-block px-10 py-4 bg-[#618A3D] text-white font-bold rounded-lg shadow-lg hover:bg-[#75a64b] transition text-lg">Get Started</a>
      </section>
    </div>
  );
}

export default Home;
